import { Modal, Table, Text, TransferTable } from '@/components';
import type {
  UserBaseModel,
  UsersLightModel,
} from '@/features/tenant-manager/user/_models/user';
import {
  type ComponentProps,
  type FC,
  type Key,
  useEffect,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';

type UserLightModel = z.infer<typeof UsersLightModel>[number];
type UserModel = z.infer<typeof UserBaseModel>;

type MembersOfOrganizationModalProps = {
  allUsers: Array<UserLightModel>;
  joinedUsers: Array<UserModel>;
  open: boolean;
  onOK: (submitUsers: string[]) => void | Promise<void>;
  onCancel: () => void;
  isLoading: boolean;
  isIntegrated: boolean;
  onlyView: boolean;
};
export const MembersOfOrganizationModal: FC<
  MembersOfOrganizationModalProps
> = ({
  allUsers,
  joinedUsers,
  open,
  onOK,
  onCancel,
  isLoading,
  isIntegrated,
  onlyView,
}) => {
  const { t } = useTranslation();
  const [currentMembers, changeCurrentMembers] = useState<string[]>([]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: 既存のDependencyListを変更した際の影響が読めないためignoreしておく。把握している人がいればignoreがなくなるよう適切に変更すること。
  useEffect(() => {
    changeCurrentMembers(joinedUsers.map((user) => user.id));
  }, [joinedUsers, open]);

  const onChangeSelect = (nextTargetKeys: Key[]) => {
    changeCurrentMembers(nextTargetKeys as string[]);
  };

  const sendOK = async () => {
    await onOK(currentMembers);
  };

  const transferTableColumns: ComponentProps<
    typeof TransferTable<UserModel | UserLightModel>
  >['columns'] = [
    {
      title: t('name'),
      dataIndex: 'name',
      render: (_, { name }) => <Text text={name} weight="regular" />,
      width: '40%',
    },
    {
      title: t('email'),
      dataIndex: 'email',
      render: (_, { email }) => <Text text={email ?? null} weight="regular" />,
      width: '60%',
    },
  ];

  return (
    <Modal
      title={t('member-of-organization')}
      open={open}
      onOk={sendOK}
      okText={t('save')}
      onCancel={onCancel}
      cancelText={t('cancel')}
      width="90%"
      confirmLoading={isLoading}
      destroyOnClose
      {...(isIntegrated || onlyView ? { footer: null } : {})}
    >
      {onlyView ? (
        <Table
          enableSticky={false}
          rowKey="id"
          columns={transferTableColumns}
          dataSource={joinedUsers}
          pagination={{ showSizeChanger: true }}
        />
      ) : (
        <TransferTable
          columns={transferTableColumns}
          dataSource={allUsers}
          rowKey={(user) => user.id}
          targetKeys={currentMembers}
          onChange={onChangeSelect}
          titles={[
            t('non-member-of-organization'),
            t('member-of-organization'),
          ]}
          render={(user) => user.name}
          showSearch
          disabled={isIntegrated || onlyView}
          tablePagination={{ showSizeChanger: true }}
        />
      )}
    </Modal>
  );
};
